/**
 * Pure domain logic for the Dual N-back test.
 *
 * All functions are deterministic when a seed is provided,
 * making the engine easy to unit-test.
 *
 * @module nback/engine
 */

import type {
    Trial,
    TrialResult,
    TestConfig,
    TestResults,
    KeyEvent,
    VisualStimulus,
    AuditoryStimulus,
} from "./types";

// ---------------------------------------------------------------------------
// Seeded PRNG (mulberry32)
// ---------------------------------------------------------------------------

/**
 * Creates a deterministic pseudo-random number generator using the mulberry32
 * algorithm. Returns a function that produces numbers in [0, 1).
 *
 * @param seed - Integer seed.
 * @returns A function that returns the next random number on each call.
 */
function mulberry32(seed: number): () => number {
    let s = seed | 0;
    return () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Picks a random element from an array using the provided random function.
 *
 * @param arr - The array to pick from.
 * @param rand - Random function returning [0, 1).
 * @returns A randomly selected element.
 */
function pick<T>(arr: readonly T[], rand: () => number): T {
    return arr[Math.floor(rand() * arr.length)] as T;
}

/**
 * Picks a random element from an array that is NOT equal to `exclude`.
 *
 * @param arr - The array to pick from.
 * @param exclude - The value to avoid.
 * @param rand - Random function returning [0, 1).
 * @returns A randomly selected element different from `exclude`.
 */
function pickDifferent<T>(
    arr: readonly T[],
    exclude: T,
    rand: () => number
): T {
    // If the array only has one element, we cannot pick a different one.
    if (arr.length <= 1) return exclude;
    let chosen: T;
    do {
        chosen = pick(arr, rand);
    } while (chosen === exclude);
    return chosen;
}

// ---------------------------------------------------------------------------
// Trial generation
// ---------------------------------------------------------------------------

/**
 * Generates a sequence of trials for the N-back test.
 *
 * The first `config.n` trials are **warm-up** and cannot contain matches.
 * For the remaining trials each modality (visual / auditory) independently
 * has approximately 25 % probability of being a match.
 *
 * The sequence is **deterministic** when a `seed` is provided, making it
 * reproducible across runs for testing and scientific rigor.
 *
 * @param config - The test configuration.
 * @param seed   - Optional integer seed for deterministic generation.
 *                 When omitted, `Date.now()` is used.
 * @returns An array of `Trial` objects.
 */
export function generateTrials(
    config: TestConfig,
    seed?: number
): readonly Trial[] {
    const rand = seed !== undefined ? mulberry32(seed) : mulberry32(Date.now());
    const trials: Trial[] = [];

    // Keep track of the last used stimulus for each modality so we can avoid
    // accidental consecutive identical stimuli (which could confuse subjects).
    let lastVisual: VisualStimulus | null = null;
    let lastAuditory: AuditoryStimulus | null = null;

    for (let i = 0; i < config.trialCount; i++) {
        const isWarmup = i < config.n;

        // --- Visual ---
        let visual: VisualStimulus;
        if (isWarmup) {
            // Warm-up: pick any stimulus different from the last one.
            visual =
                lastVisual === null
                    ? pick(config.visualStimuli, rand)
                    : pickDifferent(config.visualStimuli, lastVisual, rand);
        } else {
            const prevTrial = trials[i - config.n];
            // istanbul ignore if — guard for TypeScript; guaranteed safe by loop bounds
            if (!prevTrial) {
                visual = pick(config.visualStimuli, rand);
            } else {
                // ~25 % chance of a match
                const shouldMatch = rand() < 0.25;
                if (shouldMatch) {
                    visual = prevTrial.visual;
                } else {
                    visual = pickDifferent(
                        config.visualStimuli,
                        prevTrial.visual,
                        rand
                    );
                }
            }
        }

        // --- Auditory ---
        let auditory: AuditoryStimulus;
        if (isWarmup) {
            auditory =
                lastAuditory === null
                    ? pick(config.auditoryStimuli, rand)
                    : pickDifferent(config.auditoryStimuli, lastAuditory, rand);
        } else {
            const prevTrial = trials[i - config.n];
            // istanbul ignore if — guard for TypeScript; guaranteed safe by loop bounds
            if (!prevTrial) {
                auditory = pick(config.auditoryStimuli, rand);
            } else {
                const shouldMatch = rand() < 0.25;
                if (shouldMatch) {
                    auditory = prevTrial.auditory;
                } else {
                    auditory = pickDifferent(
                        config.auditoryStimuli,
                        prevTrial.auditory,
                        rand
                    );
                }
            }
        }

        trials.push({ index: i, visual, auditory });
        lastVisual = visual;
        lastAuditory = auditory;
    }

    return trials;
}

// ---------------------------------------------------------------------------
// Match detection
// ---------------------------------------------------------------------------

/**
 * Checks whether the visual stimulus of a trial matches its N-back predecessor.
 *
 * @param current - The current trial.
 * @param previous - The trial N steps back, or `null` if there is none.
 * @param n - The N-back level.
 * @returns `true` if the visual stimuli match.
 */
export function isVisualNBackMatch(
    current: Trial,
    previous: Trial | null
): boolean {
    if (previous === null) return false;
    return current.visual === previous.visual;
}

/**
 * Checks whether the auditory stimulus of a trial matches its N-back predecessor.
 *
 * @param current - The current trial.
 * @param previous - The trial N steps back, or `null` if there is none.
 * @param n - The N-back level.
 * @returns `true` if the auditory stimuli match.
 */
export function isAuditoryNBackMatch(
    current: Trial,
    previous: Trial | null
): boolean {
    if (previous === null) return false;
    return current.auditory === previous.auditory;
}

// ---------------------------------------------------------------------------
// Trial evaluation
// ---------------------------------------------------------------------------

/**
 * Evaluates a single trial against the collected key events.
 *
 * A key press is considered valid if it occurred between the stimulus onset
 * (`trialStartTime`) and the start of the **next** trial's stimulus
 * (i.e. within `stimulusDuration + interStimulusInterval` ms).
 *
 * Reaction time is calculated as `keyTimestamp - trialStartTime`.
 *
 * @param trial           - The trial to evaluate.
 * @param previousTrial   - The N-back predecessor, or `null` for warm-up trials.
 * @param keyEvents       - All key events captured during this response window.
 * @param trialStartTime  - High-res timestamp (ms) when this trial's stimulus appeared.
 * @param config          - The test configuration (used for N value and response window).
 * @returns A fully populated `TrialResult`.
 */
export function evaluateTrial(
    trial: Trial,
    previousTrial: Trial | null,
    keyEvents: readonly KeyEvent[],
    trialStartTime: number,
    config: TestConfig
): TrialResult {
    const responseWindow =
        config.stimulusDuration + config.interStimulusInterval;

    const isVisualMatch = isVisualNBackMatch(trial, previousTrial);
    const isAuditoryMatch = isAuditoryNBackMatch(trial, previousTrial);

    // Find valid key presses within the response window
    const visualKeyEvent = keyEvents.find(
        (e) =>
            e.key === config.visualKey &&
            e.timestamp >= trialStartTime &&
            e.timestamp < trialStartTime + responseWindow
    );
    const auditoryKeyEvent = keyEvents.find(
        (e) =>
            e.key === config.auditoryKey &&
            e.timestamp >= trialStartTime &&
            e.timestamp < trialStartTime + responseWindow
    );

    const visualKeyPressed = visualKeyEvent !== undefined;
    const auditoryKeyPressed = auditoryKeyEvent !== undefined;

    const visualReactionTime =
        visualKeyEvent !== undefined
            ? visualKeyEvent.timestamp - trialStartTime
            : null;
    const auditoryReactionTime =
        auditoryKeyEvent !== undefined
            ? auditoryKeyEvent.timestamp - trialStartTime
            : null;

    return {
        trial,
        isVisualMatch,
        isAuditoryMatch,
        visualKeyPressed,
        auditoryKeyPressed,
        visualReactionTime,
        auditoryReactionTime,
        visualHit: isVisualMatch && visualKeyPressed,
        auditoryHit: isAuditoryMatch && auditoryKeyPressed,
        visualOmission: isVisualMatch && !visualKeyPressed,
        auditoryOmission: isAuditoryMatch && !auditoryKeyPressed,
        visualCommission: !isVisualMatch && visualKeyPressed,
        auditoryCommission: !isAuditoryMatch && auditoryKeyPressed,
    };
}

// ---------------------------------------------------------------------------
// Results aggregation
// ---------------------------------------------------------------------------

/**
 * Computes standard deviation from an array of numbers.
 *
 * @param values - Array of numeric values.
 * @returns The population standard deviation, or `null` if fewer than 2 values.
 */
function stdDev(values: readonly number[]): number | null {
    if (values.length < 2) return null;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const squaredDiffs = values.map((v) => (v - mean) ** 2);
    return Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / values.length);
}

/**
 * Aggregates individual trial results into summary statistics.
 *
 * @param results - Array of trial results from evaluation.
 * @returns A `TestResults` object with aggregated metrics.
 */
export function computeResults(results: readonly TrialResult[]): TestResults {
    const visualRTs = results
        .filter((r) => r.visualHit && r.visualReactionTime !== null)
        .map((r) => r.visualReactionTime as number);
    const auditoryRTs = results
        .filter((r) => r.auditoryHit && r.auditoryReactionTime !== null)
        .map((r) => r.auditoryReactionTime as number);

    return {
        totalTrials: results.length,
        visualHits: results.filter((r) => r.visualHit).length,
        visualOmissions: results.filter((r) => r.visualOmission).length,
        visualCommissions: results.filter((r) => r.visualCommission).length,
        visualMeanRT:
            visualRTs.length > 0
                ? visualRTs.reduce((a, b) => a + b, 0) / visualRTs.length
                : null,
        visualRTStdDev: stdDev(visualRTs),
        auditoryHits: results.filter((r) => r.auditoryHit).length,
        auditoryOmissions: results.filter((r) => r.auditoryOmission).length,
        auditoryCommissions: results.filter((r) => r.auditoryCommission).length,
        auditoryMeanRT:
            auditoryRTs.length > 0
                ? auditoryRTs.reduce((a, b) => a + b, 0) / auditoryRTs.length
                : null,
        auditoryRTStdDev: stdDev(auditoryRTs),
    };
}
